<template>
    <div class="color_picker" :id="`color_picker-${colorSide}`">
        <div>
            <div
                @click="colorPickerVisible = !colorPickerVisible"
                class="color_picker-trigger"
                :class="colorPickerVisible ? 'active' : ''"
            >
                <small>{{ colorSide === 'frontColor' ? 'Front' : 'Back' }}</small>
                <span>#{{ getActiveElement[colorSide].hex }}</span>
                <div
                    style="width: 30%; height: 15px; margin: 0 0 0 5px;"
                    :style="`background-color: #${getActiveElement[colorSide].hex}`"
                />
            </div>
            <div
                class="color_picker-container"
                :id="`color_picker-container-${colorSide}`"
                v-show="colorPickerVisible"
            > 
                <div :id="`picker_size-${colorSide}`" style="width: 150px;" class="picker_size">
                    <canvas
                        :id="`color_picker-block-${colorSide}`"
                        class="color_picker-block"
                        width="150"
                        height="150"
                        @click="(e) => {changeInputColor(e)}"
                        @mousemove="mousemove"
                        @mousedown="mousedown"
                        @mouseup="mouseup"
                    />
                    <div :id="`color_picker-pointer-${colorSide}`"
                        :style="`left: ${pointerPos.x}px; top: ${pointerPos.y}px;`"
                        class="color_picker-pointer"
                    />
                </div>
                <div :id="`spectrum_size-${colorSide}`" style="width: 150px;" class="spectrum_size">
                    <canvas
                        :id="`color_picker-spectrum-${colorSide}`"
                        class="color_picker-spectrum"
                        width="150"
                        height="20"
                        @click="(e) => {changeSpectrumColor(e)}"
                    />
                </div>
                <div class="color_picker-values" style="width: 150px;">
                    <div class="hex_input">
                        <span>#</span>
                        <input
                            type="text"
                            v-model="hexColor"
                            @input="onInputChange"
                            :ref="`hex-${colorSide}`"
                        />
                    </div>
                    <div class="rgb_input">
                        <span>R <input :ref="`rgb_r-${colorSide}`" type="number" v-model.number="rgbColor.r" @input="onRgbInputChange(rgbColor.r, 'red')"/></span>
                        <span>G <input :ref="`rgb_g-${colorSide}`" type="number" v-model.number="rgbColor.g" @input="onRgbInputChange(rgbColor.g, 'green')"/></span>
                        <span>B <input :ref="`rgb_b-${colorSide}`" type="number" v-model.number="rgbColor.b" @input="onRgbInputChange(rgbColor.b, 'blue')"/></span>
                    </div>
                </div>
                <lastUsedColors :qrColorSide="colorSide" />
            </div>
        </div>
    </div>
</template>

<script>
    import { mapState, mapGetters } from 'vuex';
    import { qrSvg } from '../../../js/qrCodeSvg';
    import lastUsedColors from './lastUsedColors.vue';

    export default {
        data() {
            return {
                colorPickerVisible: false,
                isDragActive: false,
                isSpectrumDragActive: false,
                hexColor: undefined,
                rgbColor: {
                    r: undefined,
                    g: undefined,
                    b: undefined,
                    rgb: undefined
                },
                hslColor: {
                    h: undefined,
                    s: undefined,
                    l: undefined
                },
                hueColor: undefined,
                width: 150,
                height: 150,
                ctx: undefined,
                pointerPos: {
                    x: undefined,
                    y: undefined
                }
            }
        },
        methods: {
            createColorPickerSpectrum() {
                const colorPicker = document.getElementById(`color_picker-container-${this.colorSide}`);
                const colorPickerSpectrum = document.getElementById(`color_picker-spectrum-${this.colorSide}`);
                const spectrumCtx = colorPickerSpectrum.getContext('2d');

                const spectrumWidth = document.getElementById(`color_picker-spectrum-${this.colorSide}`).width;
                const spectrumHeight = document.getElementById(`color_picker-spectrum-${this.colorSide}`).height;
                spectrumCtx.rect(0, 0, spectrumWidth, spectrumHeight);

                let spectrumGradient = spectrumCtx.createLinearGradient(0, 0, spectrumWidth, 0);
                spectrumGradient.addColorStop(0, 'rgba(255, 0, 0, 1)');
                spectrumGradient.addColorStop(0.17, 'rgba(255, 255, 0, 1)');
                spectrumGradient.addColorStop(0.34, 'rgba(0, 255, 0, 1)');
                spectrumGradient.addColorStop(0.51, 'rgba(0, 255, 255, 1)');
                spectrumGradient.addColorStop(0.68, 'rgba(0, 0, 255, 1)');
                spectrumGradient.addColorStop(0.85, 'rgba(255, 0, 255, 1)');
                spectrumGradient.addColorStop(1, 'rgba(255, 0, 0, 1)');
                spectrumCtx.fillStyle = spectrumGradient;
                spectrumCtx.fill();
            },
            changeSpectrumColor(e) {
                let x = e.offsetX;
                let y = e.offsetY;

                let colorData = document.getElementById(`color_picker-spectrum-${this.colorSide}`).getContext('2d').getImageData(x, y, 1, 1).data;

                let r =  colorData[0];
                let g =  colorData[1];
                let b =  colorData[2]

                this.rgbColor = {
                    rgb: `rgb(${r}, ${g}, ${b})`,
                    r,
                    g,
                    b
                }
                this.hexColor = this.fullColorHex(r, g, b);

                this.hslColor = {
                    h: this.rgbToHsl(r, g, b).h,
                    s: this.rgbToHsl(r, g, b).s,
                    l: this.rgbToHsl(r, g, b).l
                }
                this.ctx.clearRect(0, 0, this.width, this.height);
                this.renderHSL(this.ctx, this.hslColor.h, this.width, this.height);
                this.pointerPos = {
                    x: this.getDotCoordinates(this.hslColor.s, this.hslColor.l, this.width, this.height).x,
                    y: this.getDotCoordinates(this.hslColor.s, this.hslColor.l, this.width, this.height).y
                }

                const payload = {
                    side: this.colorSide,
                    color: {
                        rgb: this.rgbColor.rgb,
                        hex: this.hexColor,
                        x: this.pointerPos.x,
                        y: this.pointerPos.y
                    }
                }

                this.$store.dispatch('QR_ELEMENT_COLOR', payload);
                this.$store.dispatch('ADD_COLOR_TO_LAST_USED', payload.color);

                const colors = this.colorSide === 'frontColor' ? { f: payload.color, b: this.getActiveElement.backColor } : { f: this.getActiveElement.frontColor, b: payload.color };

                qrSvg(this.getActiveElement.content, colors.f, colors.b, this.getActiveElement.width, this.getActiveElement.id);
            },
            hexToRgb(hex) {
                if (hex.charAt(0) === '#') {
                    hex = hex.substr(1);
                }
                if ((hex.length < 2) || (hex.length > 6)) {
                    return false;
                }
                let values = hex.split(''),
                    r,
                    g,
                    b;

                if (hex.length === 2) {
                    r = parseInt(values[0].toString() + values[1].toString(), 16);
                    g = r;
                    b = r;
                } else if (hex.length === 3) {
                    r = parseInt(values[0].toString() + values[0].toString(), 16);
                    g = parseInt(values[1].toString() + values[1].toString(), 16);
                    b = parseInt(values[2].toString() + values[2].toString(), 16);
                } else if (hex.length === 6) {
                    r = parseInt(values[0].toString() + values[1].toString(), 16);
                    g = parseInt(values[2].toString() + values[3].toString(), 16);
                    b = parseInt(values[4].toString() + values[5].toString(), 16);
                } else {
                    return false;
                }

                return this.rgbColor = {
                    rgb: `rgb(${r}, ${g}, ${b})`,
                    r,
                    g,
                    b
                }
            },
            rgbToHex(rgb) {
                let hex = Number(rgb).toString(16);
                if (hex.length < 2) {
                    hex = '0' + hex
                }
                return hex;
            },
            fullColorHex(r, g, b) {
                const red = this.rgbToHex(r);
                const green = this.rgbToHex(g);
                const blue = this.rgbToHex(b);

                return red + green + blue;
            },
            rgbToHsl(r, g, b) {
                r /= 255;
                g /= 255;
                b /= 255;
                
                let max = Math.max(r, g, b);
                let min = Math.min(r, g, b);
                let d = max - min;
                let h;
                if (d === 0) h = 0;
                else if (max === r) h = (g - b) / d % 6;
                else if (max === g) h = (b - r) / d + 2;
                else if (max === b) h = (r - g) / d + 4;
                let l = (min + max) / 2;
                let s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
                // return {
                //     h: h*60,
                //     s,
                //     l
                // };
                return this.hslColor = {
                    h: h*60,
                    s,
                    l
                }
            },
            hslToRgb(h, s, l) {
                let c = (1 - Math.abs(2 * l - 1)) * s;
                let hp = h / 60.0;
                let x = c * (1 - Math.abs((hp % 2) - 1));
                let rgb1;
                if (isNaN(h)) rgb1 = [0, 0, 0];
                else if (hp <= 1) rgb1 = [c, x, 0];
                else if (hp <= 2) rgb1 = [x, c, 0];
                else if (hp <= 3) rgb1 = [0, c, x];
                else if (hp <= 4) rgb1 = [0, x, c];
                else if (hp <= 5) rgb1 = [x, 0, c];
                else if (hp <= 6) rgb1 = [c, 0, x];
                let m = l - c * 0.5;
                return [
                    Math.round(255 * (rgb1[0] + m)),
                    Math.round(255 * (rgb1[1] + m)),
                    Math.round(255 * (rgb1[2] + m))
                ];
            },
            renderHSL(context, hue, width, height) {
                
                const imgData = context.getImageData(0, 0, width, height);
                const pixels = imgData.data;
                let x, y, rgb, o;
                for (y = 0; y < height; y++) { 
                    for (x = 0; x < width; x++) {
                        o = (y * width + x) * 4;
                        rgb = this.hslToRgb(hue, x / width, y / height);
                        if (x == 100 && y == 100) {
                            // console.log(rgb);
                        }
                        pixels[o] = rgb[0];
                        pixels[o+1] = rgb[1];
                        pixels[o+2] = rgb[2];
                        pixels[o+3] = 255;
                    }
                }
                context.putImageData(imgData, 0, 0);
            },
            getDotCoordinates(saturation, lightness, canvasWidth, canvasHeight) {
                var x = saturation * canvasWidth;
                var y = lightness * canvasHeight;
                
                return {
                    x,
                    y
                };
            },
            changeInputColor(e) {
                let x = e.offsetX;
                let y = e.offsetY;
                let colorData = this.ctx.getImageData(x, y, 1, 1).data;

                let r =  colorData[0];
                let g =  colorData[1];
                let b =  colorData[2]

                this.rgbColor = {
                    rgb: `rgb(${r}, ${g}, ${b})`,
                    r,
                    g,
                    b
                }
                this.hexColor = this.fullColorHex(r, g, b);
                this.hslColor = {
                    h: this.rgbToHsl(r, g, b).h,
                    s: this.rgbToHsl(r, g, b).s,
                    l: this.rgbToHsl(r, g, b).l
                }
                this.hueColor = this.hslToRgb(this.hslColor.h, 1, .5);

                this.pointerPos = {
                    x,
                    y
                }

                const payload = {
                    side: this.colorSide,
                    color: {
                        rgb: this.rgbColor.rgb,
                        hex: this.hexColor,
                        X: Math.round(this.pointerPos.x),
                        Y: Math.round(this.pointerPos.y)
                    }
                }

                this.$store.dispatch('QR_ELEMENT_COLOR', payload);
                this.$store.dispatch('ADD_COLOR_TO_LAST_USED', payload.color);
                const colors = this.colorSide === 'frontColor' ? { f: payload.color, b: this.getActiveElement.backColor } : { f: this.getActiveElement.frontColor, b: payload.color };

                qrSvg(this.getActiveElement.content, colors.f, colors.b, this.getActiveElement.width, this.getActiveElement.id);
            },
            onInputChange() {
                this.$nextTick(() => {
                    this.$refs[`hex-${this.colorSide}`].focus();
                });

                this.rgbColor = this.hexToRgb(this.hexColor)

                this.hslColor = {
                    h: this.rgbToHsl(this.rgbColor.r, this.rgbColor.g, this.rgbColor.b).h,
                    s: this.rgbToHsl(this.rgbColor.r, this.rgbColor.g, this.rgbColor.b).s,
                    l: this.rgbToHsl(this.rgbColor.r, this.rgbColor.g, this.rgbColor.b).l
                }
                this.ctx.clearRect(0, 0, this.width, this.height);
                this.renderHSL(this.ctx, this.hslColor.h, this.width, this.height);
                this.pointerPos = {
                    x: this.getDotCoordinates(this.hslColor.s, this.hslColor.l, this.width, this.height).x,
                    y: this.getDotCoordinates(this.hslColor.s, this.hslColor.l, this.width, this.height).y
                }

                const payload = {
                    side: this.colorSide,
                    color: {
                        rgb: this.rgbColor.rgb,
                        hex: this.hexColor,
                        X: Math.round(this.pointerPos.x),
                        Y: Math.round(this.pointerPos.y)
                    }
                }

                if (this.rgbColor) {
                    this.$store.dispatch('QR_ELEMENT_COLOR', payload);
                    this.$store.dispatch('ADD_COLOR_TO_LAST_USED', payload.color);
                }
                const colors = this.colorSide === 'frontColor' ? { f: payload.color, b: this.getActiveElement.backColor } : { f: this.getActiveElement.frontColor, b: payload.color };

                qrSvg(this.getActiveElement.content, colors.f, colors.b, this.getActiveElement.width, this.getActiveElement.id);

            },
            onRgbInputChange(rgbColor, color) {
                if (color === 'red') {
                    this.rgbColor.r = rgbColor;
                    this.$nextTick(() => {
                        this.$refs[`rgb_r-${this.colorSide}`].focus();
                    });
                } else if (color === 'green') {
                    this.rgbColor.g = rgbColor;
                    this.$nextTick(() => {
                        this.$refs[`rgb_g-${this.colorSide}`].focus();
                    });
                } else {
                    this.rgbColor.b = rgbColor;
                    this.$nextTick(() => {
                        this.$refs[`rgb_b-${this.colorSide}`].focus();
                    });
                }

                this.rgbColor.rgb = `rgb(${this.rgbColor.r}, ${this.rgbColor.g}, ${this.rgbColor.b})`
                
                this.hexColor = this.fullColorHex(this.rgbColor.r, this.rgbColor.g, this.rgbColor.b);

                this.hslColor = {
                    h: this.rgbToHsl(this.rgbColor.r, this.rgbColor.g, this.rgbColor.b).h,
                    s: this.rgbToHsl(this.rgbColor.r, this.rgbColor.g, this.rgbColor.b).s,
                    l: this.rgbToHsl(this.rgbColor.r, this.rgbColor.g, this.rgbColor.b).l
                }
                this.ctx.clearRect(0, 0, this.width, this.height);
                this.renderHSL(this.ctx, this.hslColor.h, this.width, this.height);
                this.pointerPos = {
                    x: this.getDotCoordinates(this.hslColor.s, this.hslColor.l, this.width, this.height).x,
                    y: this.getDotCoordinates(this.hslColor.s, this.hslColor.l, this.width, this.height).y
                }

                const payload = {
                    side: this.colorSide,
                    color: {
                        rgb: this.rgbColor.rgb,
                        hex: this.hexColor,
                        X: Math.round(this.pointerPos.x),
                        Y: Math.round(this.pointerPos.y)
                    }
                }

                if (this.rgbColor) {
                    this.$store.dispatch('QR_ELEMENT_COLOR', payload);
                    this.$store.dispatch('ADD_COLOR_TO_LAST_USED', payload.color);
                }
                

                const colors = this.colorSide === 'frontColor' ? { f: payload.color, b: this.getActiveElement.backColor } : { f: this.getActiveElement.frontColor, b: payload.color };

                qrSvg(this.getActiveElement.content, colors.f, colors.b, this.getActiveElement.width, this.getActiveElement.id);
            },
            mousedown(event) {
                this.isDragActive = true;
            },
            mousemove(event) {
                if (this.isDragActive) {
                    // this.changeInputColor(event)
                }
            },
            mouseup(event) {
                if (this.isDragActive) {
                    this.isDragActive = false;
                }
            }
        },
        computed: {
            ...mapGetters([
                'activeElement',
                'getActiveElement'
            ])
        },
        watch: {
            'getActiveElement': {
                deep: true,
                handler() {
                    this.ctx = document.getElementById(`color_picker-block-${this.colorSide}`).getContext('2d')
                    this.hexColor = this.getActiveElement[this.colorSide].hex;

                    if (this.rgbColor) {
                        this.rgbColor.rgb = this.getActiveElement[this.colorSide].rgb;
                        // get red green blue values from rgb string
                        let sep, rgb;

                        sep = this.getActiveElement[this.colorSide].rgb.indexOf(",") > -1 ? "," : " ";
                        rgb = this.getActiveElement[this.colorSide].rgb.substr(4).split(")")[0].split(sep);
                        
                        this.rgbColor = {
                            ...this.rgbColor,
                            r: parseInt(rgb[0]),
                            g: parseInt(rgb[1]),
                            b: parseInt(rgb[2])
                        }
                        this.hslColor = this.rgbToHsl(this.rgbColor.r, this.rgbColor.g, this.rgbColor.b)
                        this.hueColor = this.hslToRgb(this.hslColor.h, 1, .5);
                        this.ctx.clearRect(0, 0, this.width, this.height);
                        this.renderHSL(this.ctx, this.hslColor.h, this.width, this.height);
                        this.pointerPos = {
                            x: this.getActiveElement[this.colorSide].X,
                            y: this.getActiveElement[this.colorSide].Y
                        }

                        this.createColorPickerSpectrum();
                    }
                    
                }
            }
        },
        mounted() {
            this.ctx = document.getElementById(`color_picker-block-${this.colorSide}`).getContext('2d')
            this.hexColor = this.getActiveElement[this.colorSide].hex;
            this.rgbColor.rgb = this.getActiveElement[this.colorSide].rgb;
            // get red green blue values from rgb string
            let sep, rgb;

            sep = this.getActiveElement[this.colorSide].rgb.indexOf(",") > -1 ? "," : " ";
            rgb = this.getActiveElement[this.colorSide].rgb.substr(4).split(")")[0].split(sep);

            this.rgbColor = {
                ...this.rgbColor,
                r: parseInt(rgb[0]),
                g: parseInt(rgb[1]),
                b: parseInt(rgb[2])
            }
            this.hslColor = this.rgbToHsl(this.rgbColor.r, this.rgbColor.g, this.rgbColor.b)
            this.hueColor = this.hslToRgb(this.hslColor.h, 1, .5);
            this.ctx.clearRect(0, 0, this.width, this.height);
            this.renderHSL(this.ctx, this.hslColor.h, this.width, this.height);
            this.pointerPos = {
                x: this.getActiveElement[this.colorSide].X,
                y: this.getActiveElement[this.colorSide].Y
            }

            this.createColorPickerSpectrum();
        },
        props: ['colorSide'],
        components: {
            lastUsedColors
        }
    }
</script>

<style lang="scss" scoped>
    .color_picker {
        width: 130px;
        position: relative;
        font-size: 12px;
        margin: auto;
        
        small {
            font-size: 12px;
            margin-right: 8px;
            color: #707070;
            text-transform: none;
        }
    }
    .color_picker > div {
        position: relative;
        width: 100%;
        box-sizing: border-box;
    }
    .color_picker-trigger {
        display: flex;
        align-items: center;
        padding: 8px 8px;
        cursor: pointer;
        text-transform: uppercase;
        z-index: 1000;
        position: relative;
    }
    .color_picker-container {
        padding: 10px 0 0 0;
        position: absolute;
        width: 200px;
        box-sizing: border-box;
        background-color: #f5f5f5;
        box-shadow: 5px 8px 10px 0px #D4D4D4;
        z-index: 1000000;
        top: 35px;
    }
    .picker_size {
        margin: 0 auto;
        position: relative;
    }
    .color_picker-pointer {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        border: 2px white solid;
        position: absolute;
        background: transparent;
        transform: translate(-50%, -50%);
        z-index: 2;
        box-shadow: 0 0 5px 2px rgba(0, 0, 0, .05), 1px 1px 3px 1px rgba(0, 0, 0, .15);
        cursor: pointer;
    }
    .color_picker-block {
        width: 100%;
    }
    .color_picker-spectrum,
    .color_picker-block {
        border-radius: 3px;
        display: block;
    }
    .spectrum_size {
        margin: 8px auto;
    }
    .color_picker-values {
        margin: 10px auto;
    }
    .color_picker-values input {
        // max-width: 100%;
        width: 90%;
        color: #656565;
        padding: 3px;
        background-color: #EFEFEF;
        border: 1px solid #D4D4D4;
        border-radius: 4px;
    }
    .hex_input {
        margin-bottom: 10px;
    }
    .hex_input,
    .rgb_input,
    .rgb_input > span {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }
    .hex_input > span {
        margin-right: 5px;
    }
    .rgb_input > span {
        width: 30%;
    }
    .rgb_input > span > input {
        margin-left: 5px;
    }
    .rgb_input input::-webkit-outer-spin-button,
    .rgb_input input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
</style>